"use client";

import React, { useState } from "react";
import { Button } from "@/components/ui/Button";
import Link from "next/link";
import { useAuth } from "@/context/AuthContext";
import { Check, CreditCard, Loader2, ArrowLeft } from "lucide-react";
import { motion } from "framer-motion";
import { CreditsPurchasePopup } from "@/components/CreditsPurchasePopup";
import { AuthModal } from "@/components/AuthModal";

export default function PricingPage() {
    const { user, token, refreshUser } = useAuth();
    const [loadingPlan, setLoadingPlan] = useState<number | null>(null);
    const [showPurchasePopup, setShowPurchasePopup] = useState(false);
    const [creditsPurchased, setCreditsPurchased] = useState(0);
    const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
    
    // Region & Currency State
    const [currency, setCurrency] = useState("INR");
    const [countryCode, setCountryCode] = useState("IN");

    // Fetch User Location
    React.useEffect(() => {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000); // 5s timeout

        const fetchLocation = async () => {
            // Allow manual override for testing: set window.forceInternational = true in console
            if ((window as any).forceInternational) {
                console.log("Forcing International Pricing (Debug Mode)");
                setCurrency("USD");
                setCountryCode("US");
                return;
            }

            try {
                const res = await fetch("https://ipapi.co/json/", { signal: controller.signal });
                if (!res.ok) throw new Error("Failed to fetch location");
                
                const data = await res.json();
                clearTimeout(timeoutId);
                
                console.log("Detected Location:", data); // Debugging log

                if (data.country_code && data.country_code !== "IN") {
                    setCurrency("USD");
                    setCountryCode(data.country_code);
                } else {
                    setCurrency("INR");
                    setCountryCode("IN");
                }
            } catch (e) {
                // Fail silently or with warn, defaulting to INR
                console.warn("Could not detect location, defaulting to INR pricing.", e);
                setCurrency("INR");
                setCountryCode("IN");
            }
        };

        fetchLocation();

        return () => {
            clearTimeout(timeoutId);
            controller.abort();
        };
    }, []);

    const pricingTiers: any = {
        INR: {
            10: 50,
            50: 200,
            100: 300
        },
        USD: {
            10: 2.99,
            50: 8.99,
            100: 14.99
        }
    };

    const getPrice = (credits: number) => {
        return pricingTiers[currency][credits];
    };

    const plans = [
        {
            id: 1,
            credits: 10,
            price: getPrice(10),
            name: "Starter",
            features: ["10 Resume Generations", "AI Cover Letter", "PDF Export", "Unlimited history"],
            recommended: false,
            comingSoon: false,
        },
        {
            id: 2,
            credits: 50,
            price: getPrice(50),
            name: "Pro",
            features: ["50 Resume Generations", "AI Cover Letter", "Priority Support", "Advanced ATS Analysis", "Unlimited history"],
            recommended: true,
            comingSoon: false,
        },
        {
            id: 3,
            credits: 100,
            price: getPrice(100),
            name: "Power",
            features: ["100 Resume Generations", "AI Cover Letter", "Instant Support", "Advanced ATS Analysis", "Unlimited History", "Premium Templates"],
            recommended: false,
            comingSoon: true,
        }
    ];

    const loadRazorpayScript = (): Promise<boolean> => {
        return new Promise((resolve) => {
            if ((window as any).Razorpay) {
                resolve(true);
                return;
            }
            const script = document.createElement('script');
            script.src = 'https://checkout.razorpay.com/v1/checkout.js';
            script.async = true;
            script.onload = () => resolve(true);
            script.onerror = () => resolve(false);
            document.body.appendChild(script);
        });
    };

    const handlePurchase = async (credits: number, planPrice: number) => {
        if (!user) {
            setIsAuthModalOpen(true);
            return;
        }

        setLoadingPlan(credits);

        try {
            if (!token) return;

            const scriptLoaded = await loadRazorpayScript();
            if (!scriptLoaded) {
                alert("Failed to load payment gateway. Please try again.");
                setLoadingPlan(null);
                return;
            }

            // 1. Create Order
            const res = await fetch(`/api/payment/create-order`, {
                method: "POST",
                headers: {
                    "Authorization": `Bearer ${token}`,
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({ credits, currency }) 
            });

            if (!res.ok) {
                const err = await res.json();
                alert(err.error || "Failed to create order");
                setLoadingPlan(null);
                return;
            }

            const data = await res.json();

            // 2. Open Razorpay
            const options = {
                key: data.key_id,
                amount: data.amount,
                currency: data.currency,
                name: "Resume Aligned",
                description: `${credits} Credits Pack`,
                order_id: data.order_id,
                handler: async function (response: any) {
                    try {
                        const verifyRes = await fetch(`/api/payment/verify`, {
                            method: "POST",
                            headers: {
                                "Authorization": `Bearer ${token}`,
                                "Content-Type": "application/json"
                            },
                            body: JSON.stringify({
                                razorpay_order_id: response.razorpay_order_id,
                                razorpay_payment_id: response.razorpay_payment_id,
                                razorpay_signature: response.razorpay_signature
                            })
                        });

                        if (verifyRes.ok) {
                            setCreditsPurchased(credits);
                            setShowPurchasePopup(true);
                            refreshUser(); // Refresh credits in UI
                        } else {
                            alert("Payment verification failed");
                        }
                    } catch (e) {
                        console.error("Verification error", e);
                        alert("Verification error");
                    } finally {
                         setLoadingPlan(null);
                    }
                },
                prefill: {
                    email: user?.email,
                },
                theme: {
                    color: "#0f172a"
                },
                modal: {
                    ondismiss: function() {
                        setLoadingPlan(null);
                    }
                }
            };

            const rzp = new (window as any).Razorpay(options);
            rzp.open();

        } catch (e) {
            console.error("Payment error", e);
            alert("Payment error");
            setLoadingPlan(null);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white pt-32 pb-20 px-4">
            <div className="max-w-6xl mx-auto space-y-12">
                <Link 
                    href="/" 
                    className="inline-flex items-center text-slate-500 hover:text-slate-900 transition-colors"
                >
                    <ArrowLeft size={20} className="mr-2" />
                    Back to Home
                </Link>
                <div className="text-center space-y-4">
                    <h1 className="text-4xl md:text-5xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-slate-900 via-blue-800 to-slate-900 font-urbanist">
                        Simple, Transparent Pricing
                    </h1>
                    <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                        Choose the perfect credit pack for your needs. 
                        No monthly subscriptions, just pay as you go.
                    </p>
                </div>

                <div className="grid md:grid-cols-3 gap-8 items-start">
                    {plans.map((plan) => (
                        <motion.div
                            key={plan.id}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: plan.id * 0.1 }}
                            className={`relative bg-white rounded-3xl p-8 border ${
                                plan.recommended 
                                    ? "border-blue-500 shadow-xl shadow-blue-500/10 scale-105 z-10" 
                                    : "border-slate-200 shadow-lg hover:shadow-xl transition-shadow"
                            }`}
                        >
                            {plan.recommended && (
                                <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-sm font-bold px-4 py-1 rounded-full">
                                    Most Popular
                                </div>
                            )}

                            <div className="space-y-6">
                                <div>
                                    <h3 className="text-xl font-bold text-slate-900">{plan.name}</h3>
                                    <div className="mt-4 flex items-baseline">
                                        <span className="text-4xl font-bold text-slate-900">
                                            {currency === "INR" ? "₹" : "$"}{plan.price}
                                        </span>
                                        <span className="ml-2 text-slate-500 text-sm">/ pack</span>
                                    </div>
                                    <p className="mt-2 text-blue-600 font-medium bg-blue-50 inline-block px-3 py-1 rounded-full text-sm">
                                        {plan.credits} Credits
                                    </p>
                                </div>

                                <ul className="space-y-4">
                                    {plan.features.map((feature, i) => (
                                        <li key={i} className="flex items-center gap-3 text-slate-600 text-sm">
                                            <div className={`p-1 rounded-full ${plan.recommended ? 'bg-blue-100 text-blue-600' : 'bg-slate-100 text-slate-600'}`}>
                                                <Check size={14} />
                                            </div>
                                            {feature}
                                        </li>
                                    ))}
                                </ul>

                                <Button 
                                    className={`w-full rounded-full h-12 ${
                                        plan.recommended 
                                            ? "bg-gradient-to-r from-blue-600 to-indigo-600 shadow-lg shadow-blue-500/25" 
                                            : "bg-slate-900 hover:bg-slate-800"
                                    }`}
                                    onClick={() => handlePurchase(plan.credits, plan.price)}
                                    disabled={loadingPlan !== null && plan.comingSoon}
                                >
                                    {loadingPlan === plan.credits ? (
                                        <Loader2 className="animate-spin mr-2" />
                                    ) : (
                                        <CreditCard className="mr-2" size={18} />
                                    )}
                                    {loadingPlan === plan.credits ? "Processing..." : plan.comingSoon ? "Coming Soon" : "Buy Now"}
                                </Button>
                            </div>
                        </motion.div>
                    ))}
                </div>

                <div className="text-center mt-12 space-y-8">
                     <p className="text-sm text-slate-500 italic">
                        Pricing is adjusted for purchasing power parity.
                    </p>

                    <div className="bg-blue-50 rounded-2xl p-8 max-w-3xl mx-auto border border-blue-100">
                        <h3 className="text-xl font-bold text-slate-900 mb-2">Need a Custom Plan?</h3>
                        <p className="text-slate-600 mb-6">
                            For layout adjustments, bulk purchasing, or enterprise needs, get in touch with us.
                        </p>
                        <a href="mailto:support@resumealigned.com" className="text-blue-600 font-bold hover:underline">
                            Contact Support
                        </a>
                    </div>
                </div>
            </div>

            <CreditsPurchasePopup 
                isOpen={showPurchasePopup}
                onClose={() => setShowPurchasePopup(false)}
                creditsAdded={creditsPurchased}
            />
             <AuthModal isOpen={isAuthModalOpen} onClose={() => setIsAuthModalOpen(false)} />
        </div>
    );
}
